import React from 'react';
import { WarningOctagon, ArrowClockwise } from '@phosphor-icons/react';
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  Text,
  Flex,
} from '@chatsdet/ui';

type DegradedHealthMenuProps = {
  /** Fires the manual re-enable action (clears degraded health with a clean slate). */
  onReEnable: () => void;
  /** True while the re-enable request is in flight. */
  isReEnabling?: boolean;
};

/**
 * Compact degraded-health control for the Flow Designer top bar. Replaces the old
 * full-width banner: a small warning icon (with a tooltip) that opens a menu; the menu
 * item opens a confirmation dialog to re-enable the flow. A degraded flow keeps failing
 * and has been auto-skipped from test suites until it's edited (which re-enables it) or
 * re-enabled here.
 */
export function DegradedHealthMenu({ onReEnable, isReEnabling }: DegradedHealthMenuProps) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  return (
    <>
      <MenuTrigger>
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="This flow is degraded — auto-skipped in test suites because it kept failing. Edit it to fix (saving re-enables it), or re-enable to retry."
          aria-label="Flow health — degraded"
        >
          <WarningOctagon size={16} weight="fill" className="text-status-degraded" />
        </Button>
        <Menu onAction={() => setIsDialogOpen(true)}>
          <MenuItem id="re-enable">
            <Flex align="center" gap="sm">
              <ArrowClockwise size={14} />
              <Text size="sm">Re-enable flow…</Text>
            </Flex>
          </MenuItem>
        </Menu>
      </MenuTrigger>

      <DialogTrigger isOpen={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <span />
        <Dialog title="Re-enable this flow?">
          <DialogContent>
            <Text size="sm" className="text-muted-foreground">
              This clears the degraded status with a clean slate, so the flow runs in test
              suites again. If it keeps failing it will degrade again.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setIsDialogOpen(false)}
              isDisabled={isReEnabling}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              iconLeft={<ArrowClockwise size={14} />}
              isDisabled={isReEnabling}
              onPress={() => {
                onReEnable();
                setIsDialogOpen(false);
              }}
            >
              {isReEnabling ? 'Re-enabling…' : 'Re-enable'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
}
